import { api } from '../console/api.ts';
import { carrierCell, carrierName } from '../console/carriers.ts';
import { empty, field, pageHead, pill, skel } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import {
  agentName,
  date,
  esc,
  fmt,
  icon,
  lineName,
  type Money,
  person,
  when,
} from '../console/format.ts';
import { followRequest } from '../console/retry-keys.ts';
import { type Actions, current, render } from '../console/router.ts';
import { has } from '../console/session.ts';
import { $, act, centsOf, toast } from '../console/ui.ts';
import {
  dateControl,
  moneyControl,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';
import { type NoteRow, subjectNotesHtml, subjectTasksHtml, type TaskRow } from './seguimiento.ts';

/**
 * «Siniestros» (#309, ADR-0038): the claims of the policies the person may see — the carrier's
 * number and ours, the policy and the customer, what happened and when, where it stands, what was
 * approved, the deductible and what was paid — with filters (status, carrier, line, agent, dates)
 * and the follow-up of each one: its people, documents, payments and log. A claim is reported from
 * the policy («Siniestros» tab), after Nubi warns about the policy (not in force on the date,
 * receipts overdue): the carrier decides.
 */

export type ClaimAnomaly =
  | { code: 'NOT_IN_FORCE'; detail: string }
  | { code: 'OVERDUE_RECEIPTS'; count: number; oldestDaysOverdue: number };
export type ClaimRow = {
  id: string;
  number: string;
  carrierClaimRef: string | null;
  policyId: string;
  policyNumber: string;
  carrier: string;
  carrierName: string;
  productLine: string;
  productName: string;
  agentId: string;
  holderName: string | null;
  cause: string;
  occurredOn: string;
  reportedOn: string;
  place: string;
  status: string;
  subStatus: string | null;
  open: boolean;
  channel: 'INTEGRATION' | 'WORK_ORDER';
  claimed: Money | null;
  reserve: Money | null;
  approved: Money | null;
  deductible: Money | null;
  paid: Money;
  toPay: Money | null;
  resolvedOn: string | null;
  closedOn: string | null;
  anomalies: ClaimAnomaly[];
};
type LogEntry = { at: string; by: string; kind: string; detail: Record<string, unknown> };
export type ClaimDetail = ClaimRow & {
  description: string;
  risk: string | null;
  coverages: string[];
  workOrderId: string | null;
  reportedBy: string;
  rejectionReason: string | null;
  payments: { paidOn: string; beneficiary: string; amount: Money; reference: string }[];
  persons: { role: string; name: string; contact: string | null; at: string; by: string }[];
  documents: { kind: string; description: string; at: string; by: string }[];
  log: LogEntry[];
  /** Its notes (app.notes, #317: pinned first) and open tasks; the earlier notes are in its log. */
  notes?: NoteRow[];
  openTasks?: TaskRow[];
  can: { follow: boolean; manage: boolean; note?: boolean };
};
type ClaimCheck = {
  policyId: string;
  policyNumber: string;
  carrierName: string;
  currency: string;
  risk: string | null;
  coverages: string[];
  channel: 'INTEGRATION' | 'WORK_ORDER';
  causes: string[];
  anomalies: ClaimAnomaly[];
};
type LossRatio = {
  key: string;
  currency: string;
  paid: Money;
  earned: Money;
  ratio: number | null;
};
export type ClaimIndicators = {
  from: string;
  to: string;
  reported: number;
  byStatus: Record<string, number>;
  byCarrier: { key: string; reported: number; open: number }[];
  byProductLine: { key: string; reported: number; open: number }[];
  lossRatioByCarrier: LossRatio[];
  lossRatioByProductLine: LossRatio[];
  meanResolutionDays: number | null;
  resolved: number;
};

/** The status of a claim as the agency says it, and its tone. */
export const CLAIM_STATUS: Record<string, [label: string, tone: string]> = {
  REPORTED: ['Reportado', 'DRAFT'],
  IN_REVIEW: ['En revisión', 'info'],
  APPROVED: ['Aprobado', 'warn'],
  PAID: ['Pagado', 'ok'],
  REJECTED: ['Rechazado', 'bad'],
  CLOSED: ['Cerrado', 'CANCELLED'],
};
export const CAUSE: Record<string, string> = {
  COLLISION: 'Choque',
  THEFT: 'Robo',
  THIRD_PARTY_DAMAGE: 'Daños a terceros',
  MEDICAL_EXPENSES: 'Gastos médicos',
  GLASS_BREAKAGE: 'Rotura de cristales',
  HOSPITALIZATION: 'Hospitalización',
  DEATH: 'Fallecimiento',
  DISABILITY: 'Invalidez',
  FIRE: 'Incendio',
  WATER_DAMAGE: 'Daños por agua',
  NATURAL_DISASTER: 'Fenómenos naturales',
};
export const PERSON_ROLE: Record<string, string> = {
  DRIVER: 'Conductor',
  THIRD_PARTY: 'Tercero',
  ADJUSTER: 'Ajustador',
  WORKSHOP: 'Taller',
  HOSPITAL: 'Hospital',
  WITNESS: 'Testigo',
  OTHER: 'Otro',
};
export const DOCUMENT_KIND: Record<string, string> = {
  PHOTO: 'Fotos',
  POLICE_REPORT: 'Parte policial',
  INVOICE: 'Factura',
  MEDICAL_REPORT: 'Informe médico',
  ESTIMATE: 'Presupuesto',
  OTHER: 'Otro',
};
const LOG: Record<string, string> = {
  REPORTED: 'Aviso',
  CARRIER_NUMBER: 'Número de reclamo',
  STATUS: 'Estado',
  SUB_STATUS: 'Subestado',
  APPROVED: 'Aprobación',
  PAYMENT: 'Pago',
  REJECTED: 'Rechazo',
  RESERVE: 'Reserva',
  PERSON: 'Persona',
  DOCUMENT: 'Documento',
  NOTE: 'Nota',
  CUSTOMER_NOTIFIED: 'Aviso al cliente',
  CUSTOMER_NOT_NOTIFIED: 'Sin aviso al cliente',
};
const NOT_NOTIFIED: Record<string, string> = {
  NO_CONSENT: 'el contratante no aceptó mensajes por WhatsApp ni por correo',
  NO_POLICYHOLDER: 'la póliza no tiene contratante registrado',
  NOT_DELIVERED: 'el mensaje no se pudo entregar',
};

export const statusPill = (s: string) => {
  const [label, tone] = CLAIM_STATUS[s] ?? [s, 'info'];
  return pill(tone, label);
};
const pct = (r: number | null) =>
  r === null ? '—' : `${(Math.round(r * 1000) / 10).toString()} %`;

/** What Nubi warns about before a notice, in the agency's words (never a refusal). */
export function anomalyText(a: ClaimAnomaly): string {
  if (a.code === 'NOT_IN_FORCE') return `La póliza no estaba vigente en la fecha: ${a.detail}.`;
  const what = a.count === 1 ? 'un recibo vencido' : `${a.count} recibos vencidos`;
  return `La póliza tiene ${what} (el más antiguo, hace ${a.oldestDaysOverdue} días).`;
}
const warnings = (as: ClaimAnomaly[]) =>
  as.length
    ? `<div class="note warn claim-warnings" role="status" data-claim-warn>${icon('warn')}<div><strong>Revisa antes de avisar</strong><ul>${as
        .map((a) => `<li>${esc(anomalyText(a))}</li>`)
        .join(
          '',
        )}</ul><span class="muted small">Puedes avisar igual: la aseguradora decide.</span></div></div>`
    : '<div data-claim-warn></div>';

/** Who reports and follows claims (the API and Cerbos decide; this only shapes the screen). */
export const reportsClaims = () => has('agency_director', 'agency_admin', 'agent');
const manages = () => has('agency_director', 'agency_admin');

/** The amounts of a claim in one line: approved, deductible, paid. */
const amounts = (r: ClaimRow) =>
  r.approved
    ? `<span class="premium">${fmt(r.approved)}</span><div class="muted small">deducible ${fmt(r.deductible)} · pagado ${fmt(r.paid)}</div>`
    : r.claimed
      ? `<span class="muted small">reclamado</span> <span class="premium">${fmt(r.claimed)}</span>`
      : '<span class="muted small">Sin monto todavía</span>';

const td = (label: string, html: string, cls = '') =>
  `<td data-label="${esc(label)}"${cls ? ` class="${cls}"` : ''}>${html}</td>`;

/** The number a person reads: the carrier's when it gave one, ours below. */
const numbers = (r: ClaimRow) =>
  r.carrierClaimRef
    ? `<span class="mono">${esc(r.carrierClaimRef)}</span><div class="muted small mono">${esc(r.number)}</div>`
    : `<span class="mono">${esc(r.number)}</span><div class="muted small">${r.channel === 'WORK_ORDER' ? 'por orden de trabajo' : 'sin número de la aseguradora'}</div>`;

/** The claims of a list (Siniestros, a policy's tab, a customer's tab). */
export function claimsTable(rows: ClaimRow[], opts: { caption: string; policy?: boolean }) {
  const open = (r: ClaimRow) => openId === r.id;
  return `<div class="table-wrap" data-component="table"><table class="table claims-table"><caption class="sr-only">${esc(opts.caption)}</caption>
    <thead><tr><th>Siniestro</th>${opts.policy ? '' : '<th>Póliza</th><th>Cliente</th>'}<th>Qué pasó</th><th>Ocurrió</th><th>Estado</th><th class="num">Montos</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
    <tbody>${rows
      .map(
        (r) => `<tr data-claim="${esc(r.id)}" class="${open(r) ? 'open' : ''}">
      ${td('Siniestro', numbers(r))}
      ${opts.policy ? '' : td('Póliza', `<span class="mono">${esc(r.policyNumber)}</span><div class="muted small">${esc(lineName(r.productLine))} · ${carrierCell(r.carrier, r.carrierName || carrierName(r.carrier))}</div>`)}
      ${opts.policy ? '' : td('Cliente', `${r.holderName ? esc(r.holderName) : '<span class="muted">Sin contratante</span>'}<div class="muted small">${esc(agentName(r.agentId))}</div>`)}
      ${td('Qué pasó', `${esc(CAUSE[r.cause] ?? r.cause)}<div class="muted small">${esc(r.place)}</div>`)}
      ${td('Ocurrió', `<span class="nowrap">${esc(date(r.occurredOn))}</span><div class="muted small">avisado el ${esc(date(r.reportedOn))}</div>`)}
      ${td('Estado', `${statusPill(r.status)}${r.subStatus ? `<div class="muted small">${esc(r.subStatus)}</div>` : ''}${r.anomalies.length ? `<div class="muted small">${icon('warn')}con advertencias al avisar</div>` : ''}`)}
      ${td('Montos', amounts(r), 'num')}
      <td class="actions"><button type="button" class="btn sm${open(r) ? ' primary' : ''}" data-claim-open="${esc(r.id)}" aria-expanded="${open(r)}" aria-controls="claim-panel-${esc(r.id)}" aria-label="Ver el siniestro ${esc(r.carrierClaimRef ?? r.number)}">${open(r) ? 'Cerrar' : 'Ver'}</button></td>
    </tr>
    <tr class="claim-detail-row" id="claim-panel-${esc(r.id)}" ${open(r) ? '' : 'hidden'}><td colspan="${opts.policy ? 6 : 8}"><div class="claim-detail" data-claim-panel="${esc(r.id)}">${open(r) ? skel(14, '40%') : ''}</div></td></tr>`,
      )
      .join('')}</tbody></table></div>`;
}

/** A policy's «Siniestros» tab: its claims and, for who reports them, «Avisar siniestro». */
export function policyClaimsHtml(policyId: string, policyNumber: string, rows: ClaimRow[]) {
  const head = `<div class="card-head"><h3>Siniestros de la póliza</h3><span class="spacer"></span>${
    reportsClaims()
      ? `<button type="button" class="btn primary" data-claim-new="${esc(policyId)}">${icon('alert')}Avisar siniestro</button>`
      : ''
  }</div><div class="claim-form" data-claim-form="${esc(policyId)}" hidden></div>`;
  const body = rows.length
    ? claimsTable(rows, { caption: `Siniestros de la póliza ${policyNumber}`, policy: true })
    : `<p class="muted policy-note">Esta póliza no tiene siniestros avisados.</p>`;
  return `<section class="card policy-claims" aria-label="Siniestros">${head}${body}</section>`;
}

// ------------------------------------------------------------------ the screen

type Filters = {
  status: string;
  carrier: string;
  productLine: string;
  agentId: string;
  from: string;
  to: string;
};
const NO_FILTERS: Filters = {
  status: '',
  carrier: '',
  productLine: '',
  agentId: '',
  from: '',
  to: '',
};
let filters: Filters = { ...NO_FILTERS };
/** The claim whose follow-up is open (it stays open across a repaint). */
let openId: string | null = null;

/** A new session starts with no filters and nothing open. */
export function resetClaims() {
  filters = { ...NO_FILTERS };
  openId = null;
}

export const claimsQuery = (f: Filters) =>
  Object.entries({
    status: f.status,
    carrier: f.carrier,
    productLine: f.productLine,
    agentId: f.agentId,
    from: f.from,
    to: f.to,
  })
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

function kpis(rows: ClaimRow[], ind: ClaimIndicators | null) {
  const open = rows.filter((r) => r.open).length;
  const gtq = (xs: LossRatio[]) => {
    const g = xs.filter((x) => x.currency === 'GTQ');
    const paid = g.reduce((a, x) => a + x.paid.amountMinor, 0);
    const earned = g.reduce((a, x) => a + x.earned.amountMinor, 0);
    return { paid, earned, ratio: earned > 0 ? paid / earned : null };
  };
  const year = ind ? gtq(ind.lossRatioByCarrier) : null;
  const tile = (iconName: string, label: string, value: string, foot: string) =>
    `<div class="card kpi"><span class="kpi-label">${icon(iconName)}${esc(label)}</span><span class="kpi-value">${value}</span><span class="kpi-foot">${foot}</span></div>`;
  return `<section class="kpis" aria-label="Indicadores de siniestros" data-component="claim-kpis">
    ${tile('alert', 'Abiertos', String(open), open ? 'reportados, en revisión o aprobados' : 'Ninguno abierto en esta lista')}
    ${tile('cash', 'Pagado en el año', fmt({ amountMinor: year?.paid ?? 0, currency: 'GTQ' }), ind ? `desde el ${esc(date(ind.from))}` : 'Sin datos')}
    ${tile('chart', 'Siniestralidad del año', pct(year?.ratio ?? null), year?.earned ? `pagado ÷ prima devengada (${fmt({ amountMinor: year.earned, currency: 'GTQ' })})` : 'Sin prima devengada todavía')}
    ${tile('clock', 'Tiempo medio de resolución', ind?.meanResolutionDays === null || !ind ? '—' : `${ind.meanResolutionDays} días`, ind?.resolved ? `${ind.resolved === 1 ? '1 resuelto' : `${ind.resolved} resueltos`} en el año` : 'Ninguno resuelto en el año')}
  </section>`;
}

function toolbar(rows: ClaimRow[]) {
  const opts = (values: string[], labelOf: (v: string) => string, chosen: string, all: string) => [
    { value: '', label: all, selected: !chosen },
    ...values.map((v) => ({ value: v, label: labelOf(v), selected: v === chosen })),
  ];
  const uniq = (xs: string[]) => [...new Set(xs)].sort();
  const carriers = uniq([
    ...rows.map((r) => r.carrier),
    ...(filters.carrier ? [filters.carrier] : []),
  ]);
  const lines = uniq([
    ...rows.map((r) => r.productLine),
    ...(filters.productLine ? [filters.productLine] : []),
  ]);
  const agents = uniq([
    ...rows.map((r) => r.agentId),
    ...(filters.agentId ? [filters.agentId] : []),
  ]);
  return `<div class="card toolbar claims-toolbar" role="search" aria-label="Filtrar siniestros">
    ${field(
      'Estado',
      selectControl(
        { id: 'cf-status' },
        opts(Object.keys(CLAIM_STATUS), (v) => CLAIM_STATUS[v]?.[0] ?? v, filters.status, 'Todos'),
      ),
    )}
    ${field('Aseguradora', selectControl({ id: 'cf-carrier' }, opts(carriers, carrierName, filters.carrier, 'Todas')))}
    ${field('Ramo', selectControl({ id: 'cf-line' }, opts(lines, lineName, filters.productLine, 'Todos')))}
    ${has('agent') && !has('agency_director', 'agency_admin', 'agency_manager') ? '' : field('Agente', selectControl({ id: 'cf-agent' }, opts(agents, agentName, filters.agentId, 'Todos')))}
    ${field('Avisados desde', dateControl({ id: 'cf-from', value: filters.from }))}
    ${field('Hasta', dateControl({ id: 'cf-to', value: filters.to }))}
    ${Object.values(filters).some(Boolean) ? '<button type="button" class="btn sm" data-claim-clear>Quitar filtros</button>' : ''}
  </div>`;
}

export async function siniestros() {
  const query = claimsQuery(filters);
  const [list, ind] = await Promise.all([
    api<{ items: ClaimRow[]; counts: { all: number; open: number } }>(
      'GET',
      `/v1/claims${query ? `?${query}` : ''}`,
    ),
    api<ClaimIndicators>('GET', '/v1/claims/indicators').catch(() => null),
  ]);
  const rows = list.items;
  const head = pageHead(
    'Siniestros',
    has('agent') && !has('agency_director', 'agency_admin')
      ? 'Los siniestros de tus pólizas: dónde va cada uno con la aseguradora, lo aprobado y lo pagado. Se avisan desde la póliza.'
      : 'Los siniestros de la cartera: número de la aseguradora, estado, monto aprobado, deducible y pagos, y la siniestralidad del año. Se avisan desde la póliza.',
  );
  const table = rows.length
    ? claimsTable(rows, { caption: 'Siniestros' })
    : empty(
        'alert',
        Object.values(filters).some(Boolean)
          ? 'Ningún siniestro con esos filtros'
          : 'Sin siniestros',
        Object.values(filters).some(Boolean)
          ? 'Prueba con otros filtros o quítalos.'
          : 'Cuando un cliente tenga un siniestro, avísalo desde su póliza, en la pestaña «Siniestros».',
      );
  setTimeout(() => void reopen(), 0);
  return `${head}${kpis(rows, ind)}${toolbar(rows)}
    <section class="card claims" data-component="claims">
      <div class="card-head"><h2>Siniestros</h2><span class="spacer"></span><span class="muted small">${rows.length === 1 ? '1 siniestro' : `${rows.length} siniestros`}</span></div>
      ${table}</section>${lossRatios(ind)}`;
}

/** The loss ratio of the year per carrier and per line (who sees the portfolio). */
export function lossRatios(ind: ClaimIndicators | null) {
  if (!ind || (!ind.lossRatioByCarrier.length && !ind.lossRatioByProductLine.length)) return '';
  const rows = (xs: LossRatio[], name: (k: string) => string) =>
    xs
      .map(
        (x) =>
          `<tr><td>${esc(name(x.key))}${x.currency === 'USD' ? ' <span class="muted small">(dólares)</span>' : ''}</td><td class="num">${fmt(x.paid)}</td><td class="num">${fmt(x.earned)}</td><td class="num"><strong>${pct(x.ratio)}</strong></td></tr>`,
      )
      .join('');
  const table = (caption: string, first: string, body: string) =>
    `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">${esc(caption)}</caption><thead><tr><th>${esc(first)}</th><th class="num">Pagado</th><th class="num">Prima devengada</th><th class="num">Siniestralidad</th></tr></thead><tbody>${body}</tbody></table></div>`;
  return `<section class="card loss-ratio" data-component="loss-ratio" aria-label="Siniestralidad">
    <div class="card-head"><h2>Siniestralidad desde el ${esc(date(ind.from))}</h2><span class="spacer"></span><span class="muted small">pagado ÷ prima devengada (pro rata por días)</span></div>
    <div class="loss-ratio-grid">${table('Siniestralidad por aseguradora', 'Aseguradora', rows(ind.lossRatioByCarrier, carrierName))}${table('Siniestralidad por ramo', 'Ramo', rows(ind.lossRatioByProductLine, lineName))}</div>
  </section>`;
}

/** After a paint: the open claim loads its follow-up. */
async function reopen() {
  if (openId) await loadDetail(openId);
}

async function loadDetail(id: string) {
  const panel = document.querySelector<HTMLElement>(`[data-claim-panel="${CSS.escape(id)}"]`);
  if (!panel) return;
  try {
    const d = await api<ClaimDetail>('GET', `/v1/claims/${encodeURIComponent(id)}`);
    panel.innerHTML = claimDetailHtml(d);
  } catch {
    panel.innerHTML = `<p class="note">${icon('alert')}No se pudo cargar este siniestro. <button type="button" class="btn sm" data-claim-open="${esc(id)}" data-reload="1">Reintentar</button></p>`;
  }
}

const logLine = (e: LogEntry) => {
  const d = e.detail;
  const money = (k: string) => fmt(d[k] as Money | null);
  const text =
    e.kind === 'REPORTED'
      ? `Aviso ${d.channel === 'WORK_ORDER' ? `por orden de trabajo ${esc(String(d.workOrderId ?? ''))}` : 'a la aseguradora por su integración'}.`
      : e.kind === 'CARRIER_NUMBER'
        ? `La aseguradora le dio el número ${esc(String(d.carrierClaimRef))}.`
        : e.kind === 'STATUS'
          ? `De «${esc(CLAIM_STATUS[String(d.from)]?.[0] ?? d.from)}» a «${esc(CLAIM_STATUS[String(d.to)]?.[0] ?? d.to)}».`
          : e.kind === 'APPROVED'
            ? `Aprobado por ${money('approved')} con deducible de ${money('deductible')}.`
            : e.kind === 'PAYMENT'
              ? `Pago de ${money('amount')} a ${esc(String(d.beneficiary))} (ref. ${esc(String(d.reference))}).`
              : e.kind === 'REJECTED'
                ? `Rechazado: ${esc(String(d.reason))}.`
                : e.kind === 'RESERVE'
                  ? `Reserva de ${money('reserve')}.`
                  : e.kind === 'SUB_STATUS'
                    ? d.subStatus
                      ? `Subestado: ${esc(String(d.subStatus))}.`
                      : 'Sin subestado.'
                    : e.kind === 'PERSON'
                      ? `${esc(PERSON_ROLE[String(d.role)] ?? d.role)}: ${esc(String(d.name))}.`
                      : e.kind === 'DOCUMENT'
                        ? `${esc(DOCUMENT_KIND[String(d.kind)] ?? d.kind)}: ${esc(String(d.description))}.`
                        : e.kind === 'NOTE'
                          ? esc(String(d.note))
                          : e.kind === 'CUSTOMER_NOTIFIED'
                            ? `Se le avisó al cliente por ${d.channel === 'WHATSAPP' ? 'WhatsApp' : 'correo'} que quedó «${esc(CLAIM_STATUS[String(d.status)]?.[0] ?? d.status)}».`
                            : `No se le avisó al cliente: ${esc(NOT_NOTIFIED[String(d.reason)] ?? d.reason)}.`;
  const tone =
    e.kind === 'REJECTED' || e.kind === 'CUSTOMER_NOT_NOTIFIED'
      ? 'bad'
      : e.kind === 'PAYMENT'
        ? 'ok'
        : 'info';
  return `<li>${pill(tone, LOG[e.kind] ?? e.kind)}<span class="muted small">${esc(when(e.at))} · ${esc(person(e.by.replace(/^member:/, 'human:').replace(/^agent:/, '')))}</span><span class="timeline-note">${text}</span></li>`;
};

/** One claim, whole: what happened, the money, the people, the documents, its log and actions. */
export function claimDetailHtml(d: ClaimDetail) {
  const id = esc(d.id);
  const facts = `<dl class="claim-facts">
    <div><dt>Número de la aseguradora</dt><dd class="mono">${esc(d.carrierClaimRef ?? '—')}</dd></div>
    <div><dt>Número de la agencia</dt><dd class="mono">${esc(d.number)}</dd></div>
    <div><dt>Póliza</dt><dd><span class="mono">${esc(d.policyNumber)}</span> · ${esc(d.productName || lineName(d.productLine))}</dd></div>
    <div><dt>Riesgo</dt><dd>${esc(d.risk ?? '—')}</dd></div>
    <div><dt>Ocurrió</dt><dd>${esc(date(d.occurredOn))} · ${esc(d.place)}</dd></div>
    <div><dt>Avisado</dt><dd>${esc(date(d.reportedOn))}${d.channel === 'WORK_ORDER' ? ` · orden ${esc(d.workOrderId ?? '')}` : ''}</dd></div>
    <div class="wide"><dt>Descripción</dt><dd>${esc(d.description)}</dd></div>
  </dl>`;
  const money = `<dl class="claim-money">
    <div><dt>Reclamado</dt><dd class="num">${fmt(d.claimed)}</dd></div>
    <div><dt>Reserva</dt><dd class="num">${fmt(d.reserve)}</dd></div>
    <div><dt>Aprobado</dt><dd class="num">${fmt(d.approved)}</dd></div>
    <div><dt>Deducible</dt><dd class="num">${fmt(d.deductible)}</dd></div>
    <div><dt>Pagado</dt><dd class="num"><strong>${fmt(d.paid)}</strong></dd></div>
    <div><dt>Por pagar</dt><dd class="num">${fmt(d.toPay)}</dd></div>
  </dl>${d.rejectionReason ? `<p class="note bad">${icon('alert')}Rechazado por la aseguradora: ${esc(d.rejectionReason)}</p>` : ''}${d.anomalies.length ? `<div class="note warn">${icon('warn')}<div>Al avisar, Nubi advirtió:<ul>${d.anomalies.map((a) => `<li>${esc(anomalyText(a))}</li>`).join('')}</ul></div></div>` : ''}`;
  const payments = d.payments.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Pagos del siniestro</caption><thead><tr><th>Fecha</th><th>Beneficiario</th><th>Referencia</th><th class="num">Monto</th></tr></thead><tbody>${d.payments
        .map(
          (p) =>
            `<tr><td>${esc(date(p.paidOn))}</td><td>${esc(p.beneficiary)}</td><td class="mono">${esc(p.reference)}</td><td class="num">${fmt(p.amount)}</td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : '<p class="muted small">La aseguradora todavía no ha pagado.</p>';
  const persons = d.persons.length
    ? `<ul class="claim-list">${d.persons.map((p) => `<li><strong>${esc(PERSON_ROLE[p.role] ?? p.role)}</strong> · ${esc(p.name)}${p.contact ? ` · <span class="muted">${esc(p.contact)}</span>` : ''}</li>`).join('')}</ul>`
    : '<p class="muted small">Sin personas registradas.</p>';
  const documents = d.documents.length
    ? `<ul class="claim-list">${d.documents.map((x) => `<li><strong>${esc(DOCUMENT_KIND[x.kind] ?? x.kind)}</strong> · ${esc(x.description)}</li>`).join('')}</ul>`
    : '<p class="muted small">Sin documentos registrados.</p>';
  const log = `<ol class="timeline">${[...d.log].reverse().map(logLine).join('')}</ol>`;
  const actions: string[] = [];
  if (d.can.follow && d.channel === 'INTEGRATION' && d.open)
    actions.push(`<div class="claim-action"><h4>La aseguradora</h4><p class="muted small">Nubi lo sigue con los avisos de ${esc(d.carrierName)}; consúltalo ahora si lo necesitas.</p>
      <div class="toolbar"><button type="button" class="btn" data-claim-refresh="${id}">${icon('trend')}Consultar a la aseguradora</button></div></div>`);
  if (d.can.manage && d.channel === 'WORK_ORDER') {
    if (!d.carrierClaimRef)
      actions.push(
        `<div class="claim-action"><h4>Número de la aseguradora</h4><div class="toolbar"><div class="grow">${field('Número de reclamo', textControl({ id: `cr-ref-${id}`, maxlength: 60, class: 'mono' }))}</div><button type="button" class="btn" data-claim-ref="${id}">Registrar</button></div></div>`,
      );
    actions.push(decisionForm(d));
  }
  if (d.can.manage)
    actions.push(`<div class="claim-action"><h4>Reserva y subestado</h4><div class="toolbar">
      ${field('Reserva', moneyControl({ id: `cr-res-${id}`, currency: d.paid.currency === 'USD' ? 'US$' : 'Q' }))}<button type="button" class="btn" data-claim-reserve="${id}" data-currency="${esc(d.paid.currency)}">Guardar reserva</button></div>
      <div class="toolbar"><div class="grow">${field('Subestado (palabras de la aseguradora)', textControl({ id: `cr-sub-${id}`, maxlength: 80, value: d.subStatus ?? '', placeholder: 'En peritaje' }))}</div><button type="button" class="btn" data-claim-sub="${id}">Guardar</button></div></div>`);
  if (d.can.follow)
    actions.push(`<div class="claim-action"><h4>Personas, documentos y notas</h4>
      <div class="toolbar">${field(
        'Rol',
        selectControl(
          { id: `cp-role-${id}` },
          Object.entries(PERSON_ROLE).map(([value, label]) => ({ value, label })),
        ),
      )}
        <div class="grow">${field('Nombre', textControl({ id: `cp-name-${id}`, maxlength: 120 }))}</div>
        <div class="grow">${field('Contacto', textControl({ id: `cp-contact-${id}`, maxlength: 120, placeholder: 'Teléfono o correo' }))}</div>
        <button type="button" class="btn" data-claim-person="${id}">Agregar persona</button></div>
      <div class="toolbar">${field(
        'Documento',
        selectControl(
          { id: `cd-kind-${id}` },
          Object.entries(DOCUMENT_KIND).map(([value, label]) => ({ value, label })),
        ),
      )}
        <div class="grow">${field('Qué es', textControl({ id: `cd-desc-${id}`, maxlength: 200, placeholder: 'Parte de la PNC 2026-1234' }))}</div>
        <button type="button" class="btn" data-claim-doc="${id}">Registrar documento</button></div>
      <div class="toolbar"><div class="grow">${field('Nota', textControl({ id: `cn-note-${id}`, maxlength: 1000 }))}</div><button type="button" class="btn" data-claim-note="${id}">Agregar nota</button></div></div>`);
  return `<div class="claim-detail-grid">
    <div class="claim-main"><h3>${statusPill(d.status)} ${esc(CAUSE[d.cause] ?? d.cause)}${d.holderName ? ` · ${esc(d.holderName)}` : ''}</h3>${facts}${money}
      <h4>Pagos</h4>${payments}<h4>Personas involucradas</h4>${persons}<h4>Documentos</h4>${documents}</div>
    <div class="claim-side"><h4>Bitácora</h4>${log}${actions.length ? actions.join('') : `<p class="muted small">${icon('lock')}Solo lees este siniestro.</p>`}</div>
  </div>${
    d.notes || d.openTasks
      ? `<div class="follow-up claim-follow-up" data-component="follow-up">${subjectTasksHtml(d.openTasks ?? [], { kind: 'CLAIM', id: d.id }, { canCreate: d.can.follow })}${subjectNotesHtml(d.notes ?? [], { kind: 'CLAIM', id: d.id }, d.can.note === true, { form: false })}</div>`
      : ''
  }`;
}

/** What a carrier without integration decided, recorded by the administration. */
function decisionForm(d: ClaimDetail) {
  const id = esc(d.id);
  const next: [string, string][] =
    d.status === 'REPORTED'
      ? [
          ['REVIEW', 'En revisión'],
          ['APPROVE', 'Aprobado'],
          ['REJECT', 'Rechazado'],
        ]
      : d.status === 'IN_REVIEW'
        ? [
            ['APPROVE', 'Aprobado'],
            ['REJECT', 'Rechazado'],
          ]
        : d.status === 'APPROVED'
          ? [
              ['PAY', 'Pago'],
              ['CLOSE', 'Cerrado'],
            ]
          : d.status === 'PAID' || d.status === 'REJECTED'
            ? [['CLOSE', 'Cerrado']]
            : [];
  if (!next.length) return '';
  return `<div class="claim-action"><h4>Lo que respondió la aseguradora</h4>
    <p class="muted small">La aseguradora lo trabaja por orden de trabajo: registra aquí su respuesta.</p>
    <div class="toolbar">${field(
      'Decisión',
      selectControl(
        { id: `cdec-${id}` },
        next.map(([value, label]) => ({ value, label })),
      ),
    )}
      ${field('Monto (aprobado o pagado)', moneyControl({ id: `cdec-amount-${id}` }))}
      ${field('Deducible', moneyControl({ id: `cdec-ded-${id}` }))}</div>
    <div class="toolbar"><div class="grow">${field('Beneficiario o motivo de rechazo', textControl({ id: `cdec-text-${id}`, maxlength: 500 }))}</div>
      ${field('Referencia del pago', textControl({ id: `cdec-ref-${id}`, maxlength: 60 }))}
      <button type="button" class="btn" data-claim-decide="${id}" data-currency="${esc(d.paid.currency)}">Registrar</button></div></div>`;
}

// ------------------------------------------------------------------ the notice

/** The notice's form, with what Nubi warns about for the date (today until one is chosen). */
export function reportFormHtml(c: ClaimCheck, today: string) {
  const id = esc(c.policyId);
  const sym = c.currency === 'USD' ? 'US$' : 'Q';
  return `<form class="claim-report" data-claim-report-form="${id}" novalidate>
    <h4>Avisar un siniestro a ${esc(c.carrierName)}</h4>
    <p class="muted small">${c.channel === 'WORK_ORDER' ? `${esc(c.carrierName)} recibe el aviso por orden de trabajo: Nubi abre la orden y registras su número cuando lo dé.` : `${esc(c.carrierName)} lo recibe por su integración y devuelve su número de reclamo.`}${c.risk ? ` Riesgo: ${esc(c.risk)}.` : ''}</p>
    <div data-claim-warn-box>${warnings(c.anomalies)}</div>
    <div class="form-grid">
      ${field('Fecha de ocurrencia', dateControl({ id: 'cl-date', value: today, max: today, required: true }), { required: true })}
      ${field(
        'Qué pasó',
        selectControl(
          { id: 'cl-cause', required: true },
          c.causes.map((k) => ({ value: k, label: CAUSE[k] ?? k })),
        ),
        { required: true },
      )}
      ${field('Lugar', textControl({ id: 'cl-place', maxlength: 200, required: true, placeholder: 'Zona 10, Ciudad de Guatemala' }), { required: true })}
      ${field('Monto reclamado (si se sabe)', moneyControl({ id: 'cl-claimed', currency: sym }))}
    </div>
    ${field('Descripción', textareaControl({ id: 'cl-desc', maxlength: 2000, rows: 3, required: true, placeholder: 'Qué pasó, quiénes estaban, daños visibles' }), { required: true })}
    <div class="form-foot"><button type="button" class="btn" data-claim-cancel="${id}">Cancelar</button><button type="button" class="btn primary" data-claim-report="${id}" data-currency="${esc(c.currency)}">${icon('alert')}Avisar a ${esc(c.carrierName)}</button></div>
  </form>`;
}

const value = (id: string) =>
  (document.getElementById(id) as HTMLInputElement | null)?.value?.trim() ?? '';
const today = () => new Date().toISOString().slice(0, 10);
const minor = (text: string, currency: string, what: string): Money => {
  const cents = centsOf(text, false);
  if (cents === null) throw new InputError(`Escribe ${what}.`);
  return { amountMinor: cents, currency: currency === 'USD' ? 'USD' : 'GTQ' };
};

/** Runs a command of a claim and repaints its follow-up. */
function command(t: HTMLElement, busy: string, id: string, run: () => Promise<string>) {
  void act(t, busy, async () => {
    const said = await run();
    openId = id;
    await render(current, { quiet: true });
    if (current !== 'siniestros') await loadDetail(id);
    return said;
  });
}
const post = (id: string, what: string, body: unknown) =>
  api<ClaimDetail>('POST', `/v1/claims/${encodeURIComponent(id)}/${what}`, body);

export const actions: Actions = {
  click: {
    claimOpen: (t, d) => {
      const id = d.claimOpen;
      if (!id) return;
      if (d.reload) {
        void loadDetail(id);
        return;
      }
      openId = openId === id ? null : id;
      const panel = document.getElementById(`claim-panel-${id}`);
      if (!panel) return;
      for (const other of document.querySelectorAll<HTMLElement>('.claim-detail-row'))
        if (other !== panel) other.hidden = true;
      for (const b of document.querySelectorAll<HTMLButtonElement>('[data-claim-open]')) {
        const mine = b.dataset.claimOpen === openId;
        b.setAttribute('aria-expanded', String(mine));
        b.classList.toggle('primary', mine);
        b.textContent = mine ? 'Cerrar' : 'Ver';
      }
      panel.hidden = openId !== id;
      t.closest('tr')?.classList.toggle('open', openId === id);
      if (openId === id) {
        const box = panel.querySelector<HTMLElement>('[data-claim-panel]');
        if (box) box.innerHTML = skel(14, '40%');
        void loadDetail(id);
      }
    },
    claimClear: () => {
      filters = { ...NO_FILTERS };
      openId = null;
      void render('siniestros', { quiet: true });
    },
    claimNew: (t, d) => {
      const policyId = d.claimNew;
      if (!policyId) return;
      const box = document.querySelector<HTMLElement>(
        `[data-claim-form="${CSS.escape(policyId)}"]`,
      );
      if (!box) return;
      void act(t, 'Revisando la póliza antes del aviso…', async () => {
        const c = await api<ClaimCheck>(
          'GET',
          `/v1/policies/${encodeURIComponent(policyId)}/claim-check?occurredOn=${today()}`,
        );
        box.innerHTML = reportFormHtml(c, today());
        box.hidden = false;
        // The date first, once its control has rendered (a Web Awesome input upgrades later).
        setTimeout(() => {
          try {
            $('#cl-date')?.focus();
          } catch {
            // Not rendered yet: the person tabs into it.
          }
        }, 50);
        return c.anomalies.length
          ? 'Revisa las advertencias antes de avisar.'
          : 'La póliza está al día: avisa el siniestro.';
      });
    },
    claimCancel: (_, d) => {
      const box = d.claimCancel
        ? document.querySelector<HTMLElement>(`[data-claim-form="${CSS.escape(d.claimCancel)}"]`)
        : null;
      if (!box) return;
      box.hidden = true;
      box.innerHTML = '';
      document
        .querySelector<HTMLElement>(`[data-claim-new="${CSS.escape(d.claimCancel ?? '')}"]`)
        ?.focus();
    },
    claimReport: (t, d) => {
      const policyId = d.claimReport;
      if (!policyId) return;
      const currency = d.currency ?? 'GTQ';
      void act(t, 'Avisando el siniestro a la aseguradora…', async () => {
        const occurredOn = value('cl-date');
        const place = value('cl-place');
        const description = value('cl-desc');
        if (!occurredOn) throw new InputError('Elige la fecha de ocurrencia.', 'cl-date');
        if (place.length < 3) throw new InputError('Escribe dónde pasó.', 'cl-place');
        if (description.length < 3) throw new InputError('Describe qué pasó.', 'cl-desc');
        const claimedText = value('cl-claimed');
        const body = {
          occurredOn,
          cause: value('cl-cause'),
          place,
          description,
          claimed: claimedText ? minor(claimedText, currency, 'el monto reclamado') : null,
        };
        const r = await followRequest(`claim:${policyId}:${occurredOn}:${body.cause}`, (key) =>
          api<ClaimDetail>('POST', `/v1/policies/${encodeURIComponent(policyId)}/claims`, body, {
            idempotencyKey: key,
          }),
        );
        openId = r.id;
        await render(current, { quiet: true });
        return r.channel === 'WORK_ORDER'
          ? `Siniestro ${r.number} avisado por orden de trabajo ${r.workOrderId ?? ''}.`
          : `Siniestro avisado: ${r.carrierName} le dio el número ${r.carrierClaimRef ?? r.number}.`;
      });
    },
    claimRefresh: (t, d) => {
      const id = d.claimRefresh;
      if (!id) return;
      command(t, 'Consultando a la aseguradora…', id, async () => {
        const r = await post(id, 'refresh', {});
        return `Siniestro ${r.carrierClaimRef ?? r.number}: ${CLAIM_STATUS[r.status]?.[0] ?? r.status}.`;
      });
    },
    claimRef: (t, d) => {
      const id = d.claimRef;
      if (!id) return;
      command(t, 'Registrando el número de la aseguradora…', id, async () => {
        const ref = value(`cr-ref-${id}`);
        if (!ref) throw new InputError('Escribe el número de reclamo de la aseguradora.');
        await post(id, 'carrier-ref', { carrierClaimRef: ref });
        return `Número ${ref} registrado.`;
      });
    },
    claimDecide: (t, d) => {
      const id = d.claimDecide;
      if (!id) return;
      command(t, 'Registrando la respuesta de la aseguradora…', id, async () => {
        const action = value(`cdec-${id}`);
        const currency = d.currency ?? 'GTQ';
        const text = value(`cdec-text-${id}`);
        const body =
          action === 'APPROVE'
            ? {
                action,
                approved: minor(value(`cdec-amount-${id}`), currency, 'el monto aprobado'),
                deductible: minor(value(`cdec-ded-${id}`) || '0', currency, 'el deducible'),
              }
            : action === 'PAY'
              ? {
                  action,
                  payment: {
                    paidOn: today(),
                    beneficiary: text,
                    amount: minor(value(`cdec-amount-${id}`), currency, 'el monto pagado'),
                    reference: value(`cdec-ref-${id}`),
                  },
                }
              : action === 'REJECT'
                ? { action, reason: text }
                : { action };
        const r = await post(id, 'decision', body);
        return `Siniestro ${CLAIM_STATUS[r.status]?.[0]?.toLowerCase() ?? r.status}.`;
      });
    },
    claimReserve: (t, d) => {
      const id = d.claimReserve;
      if (!id) return;
      command(t, 'Guardando la reserva…', id, async () => {
        const reserve = minor(value(`cr-res-${id}`), d.currency ?? 'GTQ', 'la reserva');
        await post(id, 'reserve', { reserve });
        return `Reserva de ${fmt(reserve)} guardada.`;
      });
    },
    claimSub: (t, d) => {
      const id = d.claimSub;
      if (!id) return;
      command(t, 'Guardando el subestado…', id, async () => {
        const sub = value(`cr-sub-${id}`);
        await post(id, 'sub-status', { subStatus: sub || null });
        return sub ? `Subestado: ${sub}.` : 'Subestado quitado.';
      });
    },
    claimPerson: (t, d) => {
      const id = d.claimPerson;
      if (!id) return;
      const name = value(`cp-name-${id}`);
      if (name.length < 2) {
        toast('Escribe el nombre de la persona.', 'error');
        $(`#cp-name-${CSS.escape(id)}`)?.focus();
        return;
      }
      command(t, 'Agregando la persona…', id, async () => {
        await post(id, 'persons', {
          role: value(`cp-role-${id}`) || 'OTHER',
          name,
          contact: value(`cp-contact-${id}`) || null,
        });
        return `${name} agregado al siniestro.`;
      });
    },
    claimDoc: (t, d) => {
      const id = d.claimDoc;
      if (!id) return;
      const description = value(`cd-desc-${id}`);
      if (description.length < 3) {
        toast('Escribe qué documento es.', 'error');
        $(`#cd-desc-${CSS.escape(id)}`)?.focus();
        return;
      }
      command(t, 'Registrando el documento…', id, async () => {
        await post(id, 'documents', { kind: value(`cd-kind-${id}`) || 'OTHER', description });
        return 'Documento registrado.';
      });
    },
    claimNote: (t, d) => {
      const id = d.claimNote;
      if (!id) return;
      const note = value(`cn-note-${id}`);
      if (note.length < 3) {
        toast('Escribe la nota.', 'error');
        $(`#cn-note-${CSS.escape(id)}`)?.focus();
        return;
      }
      // A claim's notes go to the agency's notes since #317 (pinned first, 24 hours to correct).
      command(t, 'Agregando la nota…', id, async () => {
        await api('POST', '/v1/notes', { subject: { kind: 'CLAIM', id }, body: note });
        return 'Nota agregada.';
      });
    },
  },
  change: {
    'cf-status': (c) => setFilter('status', c.value),
    'cf-carrier': (c) => setFilter('carrier', c.value),
    'cf-line': (c) => setFilter('productLine', c.value),
    'cf-agent': (c) => setFilter('agentId', c.value),
    'cf-from': (c) => setFilter('from', c.value),
    'cf-to': (c) => setFilter('to', c.value),
    // A new date of occurrence: what Nubi warns about for it.
    'cl-date': (c) => {
      const form = c.closest<HTMLElement>('[data-claim-report-form]');
      const policyId = form?.dataset.claimReportForm;
      const box = form?.querySelector<HTMLElement>('[data-claim-warn-box]');
      if (!policyId || !box || !c.value) return;
      void api<ClaimCheck>(
        'GET',
        `/v1/policies/${encodeURIComponent(policyId)}/claim-check?occurredOn=${encodeURIComponent(c.value)}`,
      )
        .then((r) => {
          box.innerHTML = warnings(r.anomalies);
        })
        .catch(() => undefined);
    },
  },
};

function setFilter(k: keyof Filters, v: string) {
  if (filters[k] === v) return;
  filters = { ...filters, [k]: v };
  openId = null;
  void render('siniestros', { quiet: true }).then(() => {
    const id = {
      status: 'cf-status',
      carrier: 'cf-carrier',
      productLine: 'cf-line',
      agentId: 'cf-agent',
      from: 'cf-from',
      to: 'cf-to',
    }[k];
    $(`#${id}`)?.focus();
  });
}

/** The person who reported a claim, as the log names them. */
export const reporterName = (by: string) => person(by.replace(/^agent:/, ''));
